<template>
    <v-container fluid class="bq">
        <div class="bq-head">
            <h1 class="bq-title">{{ $t('blocked_quest.page.title') }}</h1>
            <div class="bq-sub">{{ $t('blocked_quest.page.subtitle') }}</div>
        </div>

        <div v-if="isLoading" class="bq-state">
            <v-progress-circular indeterminate size="30" color="#1e88e5" />
        </div>

        <div v-else-if="loadError" class="bq-state">
            <v-icon size="34" color="#e57373">mdi-alert-circle-outline</v-icon>
            <div class="bq-state-msg">{{ loadError }}</div>
            <v-btn small outlined class="normal-case" @click="reload">
                {{ $t('media.picker.retry') }}
            </v-btn>
        </div>

        <!-- ⚠ 空狀態是**正常**狀態，所以文案要是好消息而不是「查無資料」。
             老師從橫幅點進來看到空的，代表別人剛好把素材上架回去了——要說得出這件事。 -->
        <div v-else-if="!items.length" class="bq-state">
            <v-icon size="34" color="#66bb6a">mdi-check-circle-outline</v-icon>
            <div class="bq-state-msg">{{ $t('blocked_quest.page.empty') }}</div>
        </div>

        <template v-else>
            <div class="bq-count">{{ countText }}</div>

            <!-- 一列 = 一道被卡住的題目。整列可點，進到那一題的編輯器。 -->
            <div v-for="q in items" :key="q.quest_id" class="bq-row">
                <div class="bq-row-main">
                    <div class="bq-where">
                        {{ q.homework_name || $t('media.delist.homework_n', { n: q.diary_homework_id }) }}
                        <span v-if="q.unit != null" class="bq-unit">
                            · {{ $t('media.delist.unit_n', { n: q.unit }) }}
                        </span>
                    </div>
                    <div class="bq-quest">{{ q.quest_title || $t('blocked_quest.page.untitled') }}</div>

                    <!-- 被換掉的素材：名稱 + 縮圖。⚠ 一題可能被**多份**素材卡住
                         （翻卡牌用了兩張圖、兩張都被下架），所以這裡是 v-for 而不是單一份。 -->
                    <div class="bq-medias">
                        <div v-for="m in q.blocked_media" :key="m.media_id" class="bq-media">
                            <!-- ⚠ 用 cover 而不是 free：這裡是小方框，字表也只列前幾個字，
                                 熱區框在這個尺寸下看不清楚也不需要看清楚——老師要認的是「哪一份素材」。 -->
                            <div class="bq-media-thumb">
                                <MediaThumb :media="m" fit="cover" :icon-size="20" />
                            </div>
                            <div class="bq-media-txt">
                                <div class="bq-media-name">{{ m.title }}</div>
                                <div class="bq-media-meta">
                                    {{ $t(`media.purpose.${m.purpose}`) }}
                                    <!-- 平台下架要標出來：那決定了老師有沒有「上架」這條路。
                                         ⚠ 它蓋過 not_yours——就算素材是他自己的，被平台
                                         下架之後他一樣不能上架，那才是他需要知道的事。 -->
                                    <span v-if="isBanned(m)" class="bq-media-owner">
                                        · {{ $t('media.library.banned_tag') }}
                                    </span>
                                    <span v-else-if="!m.is_mine" class="bq-media-owner">
                                        · {{ $t('media.delist.not_yours') }}
                                    </span>
                                </div>
                            </div>
                            <!-- ⚠⚠ **`is_mine` 決定老師有幾條路可走**（ADR 0009）：
                                 是自己的素材 → 一鍵上架就解決全部被它卡住的題目，
                                 是別人的 → 只能換素材或刪題目，決定權在對方。
                                 不分岔的話老師會以為自己沒有選擇。
                                 ⚠⚠ **`isBanned` 要一起看，而且優先。** 被平台下架的素材
                                 即使是他自己的也**不能**上架（後端 `relist` 回 403）——
                                 只看 `is_mine` 的話會給他一顆按下去必定失敗的按鈕。 -->
                            <button v-if="m.is_mine && !isBanned(m)" type="button"
                                class="bq-relist"
                                :disabled="relistingId !== null" @click.stop="doRelist(m)">
                                {{ $t('media.library.relist') }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="bq-row-acts">
                    <v-btn small color="primary" depressed class="normal-case" @click="goToQuest(q)">
                        {{ $t('blocked_quest.page.fix') }}
                    </v-btn>
                    <!-- ⚠ **刪除只能放在這裡**：題目編輯器沒有刪除入口，而關卡的題目列表
                         濾掉了被停用的題目（`getUnitList` 只撈 `status = '1'`）——
                         所以不放這顆的話，這些題目在整個介面裡刪不掉。 -->
                    <button type="button" class="bq-del" @click="askDelete(q)">
                        {{ $t('blocked_quest.page.delete') }}
                    </button>
                </div>
            </div>

            <div class="bq-hint">{{ $t('blocked_quest.page.hint') }}</div>
        </template>

        <!-- ⚠ 刪題目是**不可復原**的，一定要二次確認：這一頁的另一顆按鈕（去處理）
             是安全的，兩顆長得太近，誤觸的代價差很多。 -->
        <v-dialog v-model="deleteOpen" width="420">
            <v-card>
                <v-card-title>{{ $t('blocked_quest.page.delete_title') }}</v-card-title>
                <v-card-text>
                    <div>{{ deleting && deleting.quest_title }}</div>
                    <div class="bq-del-warn">{{ $t('blocked_quest.page.delete_warn') }}</div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="deleteOpen = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="red darken-2" dark class="normal-case" :loading="isDeleting"
                        @click="doDelete">
                        {{ $t('blocked_quest.page.delete') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <SnackMsg ref="snack" />
    </v-container>
</template>

<script>
import { blockedQuestServices } from '@/services/blockedQuest.js';
import { mediaServices } from '@/services/media.js';
import { templateUnitServices } from '@/services/templateUnit.js';
import MediaThumb from '@/components/console/diary/media/MediaThumb.vue';
import { isBanned } from '@/components/console/diary/media/mediaConstants';
import SnackMsg from '@/components/console/diary/component/SnackMsg.vue';

/**
 * 被素材下架卡住的題目（F12）— 見 mangachat-studio-api `docs/plans/MediaLibrary.spec.md` §2.4。
 *
 * ⚠ **為什麼一定要有這一頁**：被停用的題目（`status = '-2'`）**不會**出現在關卡列表裡，
 * 所以沒有這一頁的話那些題目就是一批看不見的孤兒——老師知道有題目壞了（橫幅會說），
 * 但找不到是哪幾道。而另一條恢復路徑（原素材重新上架）不在他手上，素材是別人的。
 *
 * ⚠ **沒有分頁**（後端也沒做）：正常長度是 0，不是 0 的時候老師會想一次看完處理掉，
 * 而分頁會讓他以為只有那幾道。
 */
export default {
    name: 'BlockedQuests',
    components: { MediaThumb, SnackMsg },
    data() {
        return {
            items: [],
            isLoading: true,
            loadError: '',
            relistingId: null,
            deleteOpen: false,
            deleting: null,
            isDeleting: false,
        };
    },
    computed: {
        countText() {
            /// 「幾道題目、散在幾本範本」——後者是老師判斷影響面的關鍵：
            /// 都在同一本代表是一堂課壞了，散在五本代表要一本一本去修。
            const homeworks = new Set(this.items.map((q) => q.diary_homework_id));
            return this.$t('blocked_quest.page.count', {
                q: this.items.length,
                h: homeworks.size,
            });
        },
    },
    created() {
        this.reload();
    },
    methods: {
        async reload() {
            this.isLoading = true;
            this.loadError = '';
            try {
                const res = await blockedQuestServices.getList();
                this.items = res.items || [];
            } catch (err) {
                this.loadError = this.$t('blocked_quest.page.err_load');
            }
            this.isLoading = false;
        },

        goToQuest(q) {
            /// ⚠ 進去的是**題目編輯器**而不是關卡頁：被停用的題目在關卡列表裡看不到
            /// （`getUnitList` 濾 `status = '1'`），所以只能直接連到題目本身。
            /// 後端的 `getScopedQuest` 已經改成 `status != '-1'`，所以這一題載得出來。
            this.$router.push({
                name: 'UnitQuestEdit',
                params: { id: String(q.diary_homework_id), questId: String(q.quest_id) },
            });
        },

        isBanned,

        askDelete(q) {
            this.deleting = q;
            this.deleteOpen = true;
        },

        async doDelete() {
            if (!this.deleting || this.isDeleting) {
                return;
            }
            this.isDeleting = true;
            try {
                await templateUnitServices.deleteQuest(
                    this.deleting.diary_homework_id, this.deleting.quest_id);
                this.$refs.snack.showAlertMsg('success', this.$t('blocked_quest.page.deleted'));
                this.deleteOpen = false;
                this.deleting = null;
                this.$root.$emit('blocked-quest-refresh');
                await this.reload();
            } catch (err) {
                this.$refs.snack.showAlertMsg('error', this.$t('blocked_quest.page.err_delete'));
            }
            this.isDeleting = false;
        },

        async doRelist(media) {
            if (this.relistingId !== null) {
                return;
            }
            this.relistingId = media.media_id;
            try {
                const res = await mediaServices.relistMedia(media.media_id);
                /// ⚠⚠ **`still_blocked_quest_count` 一定要顯示出來**：一題可能被多份素材
                /// 卡住，只上架其中一份時那題還不會開放。說「已全部恢復」而它不是 0
                /// 就是在騙老師。
                const key = res.still_blocked_quest_count > 0
                    ? 'media.relist.done_partial'
                    : 'media.relist.done';
                this.$refs.snack.showAlertMsg('success', this.$t(key, {
                    q: res.restored_quest_count,
                    u: res.restored_unit_count,
                    s: res.still_blocked_quest_count,
                }), 4000);
                /// 上架成功後清單一定變短了，而橫幅的數字也要跟著掉。
                this.$root.$emit('blocked-quest-refresh');
                await this.reload();
            } catch (err) {
                this.$refs.snack.showAlertMsg('error', this.$t('media.relist.err_failed'));
            }
            this.relistingId = null;
        },
    },
};
</script>

<style scoped>
.bq {
    max-width: 1000px;
}

.bq-head {
    margin-bottom: 18px;
}

.bq-title {
    font-size: 20px;
    font-weight: 600;
    color: #333;
}

.bq-sub {
    font-size: 13px;
    color: #888;
    margin-top: 4px;
}

.bq-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 60px 0;
}

.bq-state-msg {
    font-size: 13px;
    color: #777;
    text-align: center;
    max-width: 420px;
}

.bq-count {
    font-size: 13px;
    color: #555;
    margin-bottom: 10px;
}

.bq-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: #fff;
    margin-bottom: 8px;
}

.bq-row-main {
    flex: 1 1 auto;
    min-width: 0;
}

.bq-where {
    font-size: 12px;
    color: #888;
}

.bq-unit {
    color: #aaa;
}

.bq-quest {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin-top: 2px;
    word-break: break-all;
}

.bq-medias {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.bq-media {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 8px;
    border: 1px solid #eee;
    border-radius: 4px;
    background: #fafafa;
    max-width: 300px;
}

.bq-media-thumb {
    /* MediaThumb 只負責畫內容，尺寸與背景由容器決定 */
    flex: 0 0 auto;
    width: 40px;
    height: 30px;
    border-radius: 3px;
    overflow: hidden;
    background: #eceff1;
    display: flex;
    align-items: center;
    justify-content: center;
}

.bq-media-txt {
    min-width: 0;
}

.bq-media-name {
    font-size: 12px;
    color: #444;
    word-break: break-all;
}

.bq-media-meta {
    font-size: 11px;
    color: #999;
}

.bq-media-owner {
    color: #e57373;
}

.bq-relist {
    flex: 0 0 auto;
    font-size: 11px;
    padding: 3px 8px;
    border: 1px solid #66bb6a;
    border-radius: 3px;
    color: #43a047;
    background: #fff;
}

.bq-relist:disabled {
    opacity: 0.5;
}

.bq-row-acts {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
}

.bq-del {
    font-size: 12px;
    padding: 4px 8px;
    border: 1px solid #ef9a9a;
    border-radius: 3px;
    color: #e53935;
    background: #fff;
}

.bq-del-warn {
    margin-top: 8px;
    font-size: 12px;
    color: #e53935;
}

.bq-hint {
    font-size: 12px;
    color: #999;
    margin-top: 14px;
    line-height: 1.7;
}
</style>
